import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';

/**
 * D4 + FRD Settlement-Ops replacement: the LSTA open-trade lifecycle queue —
 * Middle Office's loan-closing worklist. Loans have no CSD, so this queue is
 * aging + delayed-compensation economics against T+7/T+20 commencement, never
 * custodian messages. Settle / Legal Transfer / Fail live HERE (ops persona);
 * the Leveraged Loans facility card shows read-only status and deep-links in.
 */

const Wrap = styled.div`
  padding: 1.25rem;
  color: #e2e8f0;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.25rem;
`;

const Stat = styled.div<{ $color: string }>`
  background: rgba(30, 41, 59, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-left: 3px solid ${p => p.$color};
  border-radius: 10px;
  padding: 0.7rem 0.9rem;
`;

const StatLabel = styled.div`
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
`;

const StatValue = styled.div`
  font-size: 1.15rem;
  font-weight: 700;
  color: #f1f5f9;
`;

const Scroll = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.5rem 0.6rem;
  color: #64748b;
  font-size: 0.62rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 0.55rem 0.6rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  vertical-align: middle;
  white-space: nowrap;
`;

const Chip = styled.span<{ $tone: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.62rem;
  font-weight: 700;
  background: ${p => p.$tone === 'green' ? 'rgba(16,185,129,0.12)' : p.$tone === 'red' ? 'rgba(239,68,68,0.12)' : p.$tone === 'amber' ? 'rgba(245,158,11,0.12)' : 'rgba(100,116,139,0.15)'};
  color: ${p => p.$tone === 'green' ? '#10b981' : p.$tone === 'red' ? '#ef4444' : p.$tone === 'amber' ? '#f59e0b' : '#94a3b8'};
`;

const Btn = styled.button<{ $tone: string }>`
  padding: 3px 9px;
  margin-right: 4px;
  border-radius: 4px;
  border: 1px solid ${p => p.$tone === 'red' ? 'rgba(239,68,68,0.4)' : p.$tone === 'green' ? 'rgba(16,185,129,0.4)' : 'rgba(59,130,246,0.4)'};
  background: ${p => p.$tone === 'red' ? 'rgba(239,68,68,0.1)' : p.$tone === 'green' ? 'rgba(16,185,129,0.1)' : 'rgba(59,130,246,0.1)'};
  color: ${p => p.$tone === 'red' ? '#ef4444' : p.$tone === 'green' ? '#10b981' : '#60a5fa'};
  font-size: 0.65rem;
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const Modal = styled.div`
  margin: 0 0 1rem;
  padding: 0.9rem 1.1rem;
  background: rgba(30, 41, 59, 0.7);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 10px;
  display: flex;
  gap: 0.7rem;
  align-items: flex-end;
  flex-wrap: wrap;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Label = styled.label`
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
`;

const Input = styled.input`
  background: rgba(2, 6, 23, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 6px;
  color: #e2e8f0;
  font-size: 0.75rem;
  padding: 5px 8px;
  min-width: 200px;
`;

interface QueueRow {
  tradeId: number;
  direction: string;
  securityName: string;
  cusip: string;
  portfolioName?: string;
  par: number;
  cleanPrice?: number;
  tradeDate: string;
  expectedSettleDate?: string;
  settlementStatus?: string;
  legalTransferStatus?: string;
  settlementConvention?: string;
  exceptionReason?: string;
  businessDaysOpen: number;
  delayedCompCommencement: string;
  delayedCompDays: number;
  delayedCompCostOfCarry: number;
  bisoEligible: boolean;
  phase: 'IN_WINDOW' | 'DELAYED_COMP' | 'BISO_ELIGIBLE';
}

const fmtMoney = (v?: number) => v == null ? '—'
  : Math.abs(v) >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : `$${Math.round(v).toLocaleString()}`;

const phaseChip = (r: QueueRow) => r.phase === 'IN_WINDOW'
  ? <Chip $tone="green">IN WINDOW</Chip>
  : r.phase === 'BISO_ELIGIBLE'
    ? <Chip $tone="red">BISO ELIGIBLE</Chip>
    : <Chip $tone="amber">DELAYED COMP</Chip>;

const LoanSettlementQueueScreen: React.FC = () => {
  const [rows, setRows] = useState<QueueRow[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [notice, setNotice] = useState<{ tone: string; message: string } | null>(null);
  const [modal, setModal] = useState<{ type: 'settle' | 'fail' | 'legal'; trade: QueueRow } | null>(null);
  const [fieldValue, setFieldValue] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get('/api/credit/ll/settlement/queue');
      setRows(Array.isArray(res.data) ? res.data : []);
      setLoadError(false);
    } catch {
      setRows([]);
      setLoadError(true);
    }
  };

  useEffect(() => { load(); }, []);

  const openModal = (type: 'settle' | 'fail' | 'legal', trade: QueueRow) => {
    setModal({ type, trade });
    setFieldValue(type === 'settle' ? (trade.expectedSettleDate || '') : '');
    setNotice(null);
  };

  const submitModal = async () => {
    if (!modal) return;
    setBusy(true);
    try {
      const { type, trade } = modal;
      if (type === 'settle') {
        await axios.post(`/api/credit/ll/settlement/trades/${trade.tradeId}/settle`,
          fieldValue ? { actualSettleDate: fieldValue } : {});
        setNotice({ tone: 'green', message: `Trade #${trade.tradeId} settled — cash booked, settle-basis exposure activated.` });
      } else if (type === 'fail') {
        await axios.post(`/api/credit/ll/settlement/trades/${trade.tradeId}/fail`, { reason: fieldValue });
        setNotice({ tone: 'red', message: `Trade #${trade.tradeId} marked FAILED — ${fieldValue}` });
      } else {
        await axios.post(`/api/credit/ll/settlement/trades/${trade.tradeId}/legal-transfer`,
          { status: 'EFFECTIVE', evidenceRef: fieldValue });
        setNotice({ tone: 'green', message: `Trade #${trade.tradeId} legal transfer recorded — ${fieldValue}` });
      }
      setModal(null);
      setFieldValue('');
      await load();
    } catch (e: any) {
      setNotice({ tone: 'red', message: e?.response?.data?.error || e?.message || 'Action failed.' });
    } finally {
      setBusy(false);
    }
  };

  const accruing = rows.filter(r => r.phase !== 'IN_WINDOW');
  const totalCarry = rows.reduce((s, r) => s + (r.delayedCompCostOfCarry || 0), 0);

  return (
    <Wrap>
      <div style={{ marginBottom: '1rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700 }}>Loan Settlement Queue</h2>
        <div style={{ color: '#64748b', fontSize: '0.75rem', marginTop: 2 }}>
          LSTA open-trade lifecycle — aging vs T+7/T+20 delayed-comp commencement, cost of carry at SOFR + 11.448bps, BISO at T+20.
        </div>
      </div>

      {loadError && (
        <div style={{ marginBottom: '1rem', padding: '0.6rem 0.9rem', borderRadius: 8, border: '1px solid rgba(239,68,68,0.35)', background: 'rgba(239,68,68,0.1)', color: '#fca5a5', fontSize: '0.8rem' }}>
          ⚠ Couldn't load the settlement queue — the request failed. This is a load error, not an empty queue.
        </div>
      )}
      {notice && (
        <div style={{ marginBottom: '1rem', padding: '0.6rem 0.9rem', borderRadius: 8, fontSize: '0.8rem', border: `1px solid ${notice.tone === 'green' ? 'rgba(16,185,129,0.35)' : 'rgba(239,68,68,0.35)'}`, background: notice.tone === 'green' ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)', color: notice.tone === 'green' ? '#6ee7b7' : '#fca5a5' }}>
          {notice.message}
        </div>
      )}

      <StatsRow>
        <Stat $color="#3b82f6"><StatLabel>Open Trades</StatLabel><StatValue>{rows.length}</StatValue></Stat>
        <Stat $color="#10b981"><StatLabel>In Window</StatLabel><StatValue>{rows.length - accruing.length}</StatValue></Stat>
        <Stat $color="#f59e0b"><StatLabel>Accruing Delayed Comp</StatLabel><StatValue>{accruing.length}</StatValue></Stat>
        <Stat $color="#ef4444"><StatLabel>BISO Eligible</StatLabel><StatValue>{rows.filter(r => r.bisoEligible).length}</StatValue></Stat>
        <Stat $color="#8b5cf6"><StatLabel>Cost of Carry</StatLabel><StatValue>{fmtMoney(totalCarry)}</StatValue></Stat>
      </StatsRow>

      {modal && (
        <Modal>
          <div style={{ fontSize: '0.8rem', fontWeight: 700 }}>
            {modal.type === 'settle' ? 'Settle' : modal.type === 'fail' ? 'Mark Failed' : 'Record Legal Transfer'} — trade #{modal.trade.tradeId} ({modal.trade.securityName})
          </div>
          <Field>
            <Label>
              {modal.type === 'settle' ? 'Actual settle date' : modal.type === 'fail' ? 'Failure reason *' : 'Evidence / agent consent ref *'}
            </Label>
            <Input
              type={modal.type === 'settle' ? 'date' : 'text'}
              value={fieldValue}
              onChange={e => setFieldValue(e.target.value)}
              placeholder={modal.type === 'legal' ? 'e.g. ClearPar assignment id' : undefined}
            />
          </Field>
          <Btn $tone="blue" onClick={() => setModal(null)} disabled={busy}>Cancel</Btn>
          <Btn $tone={modal.type === 'fail' ? 'red' : 'green'} onClick={submitModal}
            disabled={busy || (modal.type !== 'settle' && !fieldValue.trim())}>
            {busy ? 'Working…' : 'Confirm'}
          </Btn>
        </Modal>
      )}

      <Scroll>
        <Table>
          <thead>
            <tr>
              <Th>Trade</Th><Th>Security</Th><Th>Portfolio</Th><Th>Side</Th><Th>Par</Th><Th>Price</Th>
              <Th>Trade Date</Th><Th>Expected</Th><Th>BD Open</Th><Th>Phase</Th><Th>Delayed Comp</Th>
              <Th>Legal Transfer</Th><Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.tradeId}>
                <Td style={{ fontWeight: 700 }}>#{r.tradeId}</Td>
                <Td title={r.cusip}>{r.securityName}</Td>
                <Td>{r.portfolioName || '—'}</Td>
                <Td><Chip $tone={r.direction === 'SELL' ? 'red' : 'green'}>{r.direction}</Chip></Td>
                <Td>{fmtMoney(r.par)}</Td>
                <Td>{r.cleanPrice != null ? Number(r.cleanPrice).toFixed(3) : '—'}</Td>
                <Td>{r.tradeDate}</Td>
                <Td>{r.expectedSettleDate || '—'}</Td>
                <Td>{r.businessDaysOpen}</Td>
                <Td>
                  {phaseChip(r)}
                  {r.settlementStatus === 'FAILED' && <Chip $tone="red" style={{ marginLeft: 4 }}>FAILED</Chip>}
                  {r.exceptionReason && <span title={r.exceptionReason} style={{ marginLeft: 4, cursor: 'help' }}>⚠️</span>}
                </Td>
                <Td>
                  {r.delayedCompDays > 0
                    ? <span style={{ color: '#f59e0b' }}>{r.delayedCompDays}d · {fmtMoney(r.delayedCompCostOfCarry)}</span>
                    : <span style={{ color: '#475569' }}>starts {r.delayedCompCommencement}</span>}
                </Td>
                <Td>
                  <Chip $tone={r.legalTransferStatus === 'EFFECTIVE' ? 'green' : 'amber'}>
                    {(r.legalTransferStatus || 'PENDING CONSENT').replace(/_/g, ' ')}
                  </Chip>
                </Td>
                <Td>
                  <Btn $tone="green" disabled={r.legalTransferStatus === 'EFFECTIVE'} onClick={() => openModal('legal', r)}>Legal Transfer</Btn>
                  <Btn $tone="green" onClick={() => openModal('settle', r)}>Settle</Btn>
                  <Btn $tone="red" disabled={r.settlementStatus === 'FAILED'} onClick={() => openModal('fail', r)}>Fail</Btn>
                </Td>
              </tr>
            ))}
            {rows.length === 0 && !loadError && (
              <tr><Td colSpan={13} style={{ textAlign: 'center', color: '#475569', padding: '1.5rem' }}>
                No open loan settlements — every posted LL trade has settled.
              </Td></tr>
            )}
          </tbody>
        </Table>
      </Scroll>
    </Wrap>
  );
};

export default LoanSettlementQueueScreen;
